import React from "react";
import { Link } from "react-router-dom";
import Button from "../inputs/Button";
import Badge from "../shared/Badge";
import EmptyState from "../shared/EmptyState";

const ClientDashboardView = ({ user, data, onNavigate, onRefresh }) => {
  const { statistics = {}, panels = [] } = data || {};

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Client Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back, {user?.name}</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Panels</p>
              <p className="text-3xl font-bold text-gray-900">
                {statistics.totalPanels || 0}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Panels</p>
              <p className="text-3xl font-bold text-green-600">
                {statistics.activePanels || 0}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed Panels</p>
              <p className="text-3xl font-bold text-purple-600">
                {statistics.completedPanels || 0}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming Deadlines</p>
              <p className="text-3xl font-bold text-orange-600">
                {statistics.upcomingDeadlines || 0}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Panels */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Your Recent Panels
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {panels.length > 0 ? (
            panels.slice(0, 5).map((panel) => (
              <div key={panel.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {panel.panelName}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {panel.panelType}
                    </p>
                    <div className="flex items-center mt-2 space-x-4">
                      <Badge
                        variant={
                          (panel.panelStatus || panel.status) === "Active"
                            ? "primary"
                            : (panel.panelStatus || panel.status) === "Closed"
                            ? "success"
                            : (panel.panelStatus || panel.status) ===
                              "PUBLISHED"
                            ? "purple"
                            : "default"
                        }
                        size="sm"
                      >
                        {(
                          panel.panelStatus ||
                          panel.status ||
                          "Active"
                        ).replace("_", " ")}
                      </Badge>
                      {panel.deadline && (
                        <span className="text-sm text-gray-500">
                          Deadline:{" "}
                          {new Date(panel.deadline).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link to={`/panel-details/${panel.id}`}>
                    <Button
                      variant="primary"
                      size="md"
                      className="ml-4 bg-custom-blue hover:bg-custom-light-blue"
                    >
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8">
              <EmptyState
                title="No Panels"
                message="You don't have any panels yet."
                className="shadow-none"
              />
            </div>
          )}
        </div>
        {panels.length > 5 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Link
              to="/panels"
              className="text-custom-blue hover:text-custom-light-blue font-medium"
            >
              View all panels →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDashboardView;
